import type { ButtonProps } from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import type { MouseEvent, ReactNode, SyntheticEvent } from 'react';
import { forwardRef, useId, useState } from 'react';
import { sizes, spacingTokens } from '../../theme/tokens/index.ts';
import { Checkbox } from '../Checkbox/index.ts';
import type { MenuProps } from '../Menu/index.ts';
import { Menu, MenuItem } from '../Menu/index.ts';
import { Skeleton } from '../Skeleton/index.ts';
import type { FilterDropdownPanelProps, FilterSurfacePaperProps } from './FilterDropdownPanel.tsx';
import { FilterDropdownPanel } from './FilterDropdownPanel.tsx';
import type { FilterMaxWidth } from './FilterTrigger.tsx';
import { FilterTrigger } from './FilterTrigger.tsx';

// Ragged widths so a loading menu reads as a list of names, not as a progress bar. The inline
// Menu sizes to its anchor, so these are px rather than the panel's percentages. tokens-ok
const MENU_SKELETON_ROWS = [120, 88, 104] as const;

const StyledMenuLoadingRows = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: spacingTokens.sp8,
  width: '100%',
  paddingTop: spacingTokens.sp4,
  paddingBottom: spacingTokens.sp4,
});

/** A single selectable value in the dropdown. */
export interface FilterOption {
  /** Visible label. */
  label: ReactNode;
  /** Stable value committed via `onChange`. */
  value: string;
  /**
   * Second line under the label (Figma 3298:5568 — "Subline item"), for the detail that tells two
   * similar options apart: an id, an owner, a date. `captionSm` in `text.secondary`, and it
   * follows the label's colour once the row is selected.
   *
   * Optional, and worth leaving out when it has nothing to say — a row of repeated filler makes
   * the list taller and slower to scan, which is the opposite of what a subline is for.
   */
  description?: ReactNode;
  disabled?: boolean;
}

/** Selection value: `string[]` when `multiple`, otherwise `string | null`. */
export type FilterDropdownValue = string | string[] | null;

export interface FilterDropdownButtonProps
  extends Omit<ButtonProps, 'variant' | 'endIcon' | 'children' | 'onChange' | 'value' | 'defaultValue'> {
  /** Trigger label — the filter's name, e.g. "Format". */
  label: ReactNode;
  /**
   * Options rendered inside the dropdown. Omit when passing custom `menuContent`
   * (then selection is managed by the consumer). Required in `panel` mode.
   */
  options?: FilterOption[];
  /**
   * Multi-select (checkboxes, surface stays open) when true (default), or single-select (pick
   * commits and closes, chosen row highlighted) when false. Honoured on **both** surfaces — in
   * `panel` mode it was ignored until 2026-09-21. This does NOT decide whether the trigger shows
   * a value or a count — the number of *applied* values does (see the display rule on the
   * component).
   */
  multiple?: boolean;
  /**
   * Render the rich dropdown panel (a search field over a scrolling list) instead of the inline
   * Menu — Figma FilterDropdownPanel. Reach for it once the list is long enough that scanning it
   * beats scrolling it.
   *
   * Honours `multiple`: **multi** gives checkbox rows with a staged draft and an Apply/Clear
   * footer; **single** gives a searchable list whose pick commits and closes, with no footer —
   * staging one choice behind an Apply button only adds a step and invites "apply nothing".
   */
  panel?: boolean;
  /** In `panel` mode: show the search field (default true). */
  searchable?: boolean;
  /** Controlled selection. `string[]` for multiple, `string | null` for single. */
  value?: FilterDropdownValue;
  /** Uncontrolled initial selection. */
  defaultValue?: FilterDropdownValue;
  /** Called when the selection changes. MUI convention: `(event, value)`. */
  onChange?: (event: SyntheticEvent, value: FilterDropdownValue) => void;
  /**
   * Customise the single applied value shown in the trigger. Only consulted when exactly one
   * value is applied; defaults to that option's `label`.
   */
  renderValue?: (value: string, option: FilterOption | undefined) => ReactNode;
  /**
   * Force what the trigger displays, whatever the selection. The escape hatch for `menuContent`
   * (where this component can't know the labels) and for async option lists.
   */
  displayValue?: ReactNode;
  /**
   * Widest the whole trigger may get before the value ellipsises — a step of `sizes.maxWidth`.
   * Default `xs` (240).
   */
  maxWidth?: FilterMaxWidth;
  /**
   * Count shown in the badge. Defaults to the number of applied values. Set explicitly to
   * override, or when driving the Menu with custom `menuContent`.
   */
  count?: number;
  /** Force the active (primary) treatment. Defaults to "something is applied". */
  active?: boolean;
  /**
   * Single-select only. Clicking the row that is **already applied** clears the filter
   * (`onChange` fires with `null`). Default **true**.
   *
   * Why on by default: a single-select filter with no way back to "nothing applied" is a defect,
   * not a configuration — consumers were already emulating this by treating a repeated value as
   * "clear", invisibly. Turn it off (`allowDeselect={false}`) only for a filter that must always
   * hold a value, e.g. a sort order, where "none" is not a state the screen can render.
   *
   * With it off, clicking the applied row is a true no-op and deliberately fires **no**
   * `onChange` — filter state usually lives in a URL or a store, where a no-change event still
   * costs a re-render or a refetch.
   *
   * Trade-off worth knowing: nothing in the surface advertises that clicking a selected row
   * unsets it. The alternative considered was a leading "Any" row, which is self-explanatory at
   * the cost of a list row; the designer chose this (2026-09-21).
   */
  allowDeselect?: boolean;
  /** Called when the dropdown opens / closes (the open state is managed internally). */
  onOpen?: () => void;
  onClose?: () => void;
  /**
   * Labels for the APPLIED values, for when the option list can't supply them.
   *
   * The trigger names a single applied value by looking it up in `options`. With a server-driven
   * list the applied option is usually **not in the current page of results** — the user picked
   * "Katherine Johnson", then typed something else — so the trigger would fall back to the raw
   * value and show an opaque identifier. Pass the applied options here and they survive any
   * change to `options`.
   *
   * This is the proper fix; `displayValue` is a patch that only covers one value at a time.
   */
  selectedOptions?: FilterOption[];
  /**
   * Options are still loading. The surface opens and shows skeleton rows rather than staying
   * shut — see the note on `FilterDropdownPanel.loading`. Distinct from `disabled`, which is for
   * a filter the user may not use at all.
   */
  loading?: boolean;
  /**
   * `panel` mode: the typed query, debounced, for filters whose options come from a request.
   * Passing it switches off local filtering. See `FilterDropdownPanel` for the full contract.
   */
  onSearchChange?: (query: string) => void;
  searchDebounceMs?: number;
  minSearchLength?: number;
  /** Shown below the search threshold — the other half of "no results". */
  hintText?: string;
  loadingLabel?: string;
  /** `panel` mode: labels/placeholder for the search field and footer buttons. */
  searchPlaceholder?: string;
  applyLabel?: string;
  clearLabel?: string;
  noResultsText?: string;
  /**
   * Custom Menu content, instead of `options` (non-panel mode only). Named explicitly —
   * `children` is intentionally NOT supported (on a button-like component it would read as
   * the trigger label, which is the `label` prop).
   */
  menuContent?: ReactNode;
  /**
   * Escape hatch to the SURFACE. Leftover props already reach the trigger (that is the family's
   * "unknown props reach the root element" rule), but until this existed the `Menu` and the
   * panel's `Popover` took fixed props and nothing else — so reaching a width, a placement or a
   * test hook on them meant a global theme override, which then hit every menu in the subtree
   * rather than the one filter that needed it.
   *
   * - `menu` — the inline `Menu` (inline mode only; ignored when `panel` is set).
   * - `panel` — the `FilterDropdownPanel` (panel mode only; ignored otherwise).
   * - `paper` — the surface's `Paper` in EITHER mode. The common case, and the reason it is
   *   hoisted out of the other two: a minimum width on a short-labelled filter's menu. Applied
   *   after `menu.slotProps.paper`, so it wins if both are given.
   *
   * The ARIA wiring this component owns is not reachable through any of them — the surface `id`
   * the trigger's `aria-controls` points at, the list `role`, `aria-labelledby`, and
   * `open`/`anchorEl`/`onClose`. Those are applied last. Making them overridable would hand the
   * accessibility contract to the consumer, which is the one thing the wrapper exists to keep.
   *
   * Callback-form `slotProps` on the inner `Menu` (`slotProps: { list: (ownerState) => … }`) is
   * not supported — the object form is merged, a callback would drop the internal ids. Same
   * limitation, and the same reason, as `Accordion`'s `slotProps.region`.
   */
  slotProps?: {
    menu?: Partial<Omit<MenuProps, 'open' | 'anchorEl' | 'onClose' | 'children'>>;
    panel?: Partial<
      Omit<
        FilterDropdownPanelProps,
        'open' | 'anchorEl' | 'onClose' | 'options' | 'selected' | 'onApply' | 'onClearAll'
      >
    >;
    paper?: FilterSurfacePaperProps;
  };
}

/**
 * FilterDropdownButton — the filter-bar trigger for values picked from a list (Figma trigger:
 * FilterButtonDropdown 1264:4620).
 *
 * **Display rule (the whole family follows it):** nothing applied → the label alone; **one** value
 * applied → that value is named in the trigger ("Status: Active"); **several** → a count badge
 * ("Format 3"). One value is worth naming, several are only worth counting. The rule is driven by
 * the applied selection, not by `multiple` — a multi-select filter narrowed to one value names it.
 *
 * Two surfaces: the inline themed `Menu` (default), or the rich `FilterDropdownPanel` (search +
 * Apply/Clear footer, staged selection) when `panel` is set. Open/anchor state, keyboard
 * navigation, focus return and ARIA come from MUI; this wrapper adds the selection models and
 * decides value-vs-count, while `FilterTrigger` paints it.
 *
 * Composition — SHARED: `Button`→`ButtonBase` and `Tooltip`→`Popper` (both via `FilterTrigger`);
 * `Menu`/`Popover`→`Paper`/`Modal` (styled via slots, never global `MuiPaper`).
 * LOCAL: `FilterTrigger`, `FilterBadge` (inside the trigger), `FilterDropdownPanel`.
 * Reused: `MenuItem`, `Checkbox`.
 */
export const FilterDropdownButton = forwardRef<HTMLButtonElement, FilterDropdownButtonProps>(
  function FilterDropdownButton(
    {
      label,
      options,
      multiple = true,
      panel = false,
      searchable = true,
      value,
      defaultValue,
      onChange,
      renderValue,
      displayValue,
      maxWidth,
      count,
      active,
      allowDeselect = true,
      onOpen,
      onClose,
      onClick,
      selectedOptions,
      loading = false,
      onSearchChange,
      searchDebounceMs,
      minSearchLength,
      hintText,
      loadingLabel,
      searchPlaceholder,
      applyLabel,
      clearLabel,
      noResultsText,
      menuContent,
      slotProps,
      ...rest
    }: FilterDropdownButtonProps,
    ref
  ) {
    const buttonId = useId();
    const surfaceId = useId();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    // The panel used to be hard-wired to multi-select (`panel ? true : multiple`), which left a
    // filter holding ONE value out of hundreds with no searchable surface at all — the choice was
    // an unsearchable inline Menu hundreds of rows long, or faking multi-select over a
    // single-value contract. `multiple` now means the same thing on both surfaces.
    const multi = multiple;

    // Uncontrolled selection fallback.
    const [internalValue, setInternalValue] = useState<FilterDropdownValue>(defaultValue ?? (multi ? [] : null));
    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;

    const selectedArray = multi && Array.isArray(currentValue) ? currentValue : [];
    const selectedSingle = !multi && typeof currentValue === 'string' ? currentValue : null;

    const commit = (next: FilterDropdownValue, event: SyntheticEvent) => {
      if (!isControlled) setInternalValue(next);
      onChange?.(event, next);
    };

    const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      onOpen?.();
      onClick?.(event);
    };

    const handleClose = () => {
      setAnchorEl(null);
      onClose?.();
    };

    const handleToggleMultiple = (optionValue: string, event: SyntheticEvent) => {
      const next = selectedArray.includes(optionValue)
        ? selectedArray.filter(v => v !== optionValue)
        : [...selectedArray, optionValue];
      commit(next, event);
      // Multi-select keeps the menu open so several values can be picked.
    };

    const handleSelectSingle = (optionValue: string, event: SyntheticEvent) => {
      if (selectedSingle === optionValue) {
        // The row that is already applied. Until 2026-09-21 this re-committed the same value, so a
        // single-select filter had no path back to "nothing applied" without leaving the component
        // — the one state the user most often wants next.
        if (allowDeselect) {
          commit(null, event);
        }
        // With `allowDeselect={false}` this is a genuine no-op, so it must NOT fire `onChange`.
        // Filter state usually lives in a URL or a store; an event that carries no change still
        // costs a re-render, a history entry, or a refetch.
        handleClose();
        return;
      }
      commit(optionValue, event);
      handleClose();
    };

    const selectedCount = count ?? (multi ? selectedArray.length : selectedSingle != null ? 1 : 0);

    // The one applied value, when there is exactly one — this is what makes the trigger name it
    // instead of counting it. `null` for zero or several.
    const soleValue = multi ? (selectedArray.length === 1 ? selectedArray[0] : null) : selectedSingle;

    const resolvedValue = (() => {
      if (displayValue !== undefined) return displayValue;
      if (soleValue == null) return undefined;
      // `selectedOptions` FIRST: with a server-driven list the applied option is usually absent
      // from the page currently in `options`, and looking there first is what made the trigger
      // fall back to showing a raw id.
      const option = selectedOptions?.find(o => o.value === soleValue) ?? options?.find(o => o.value === soleValue);
      if (renderValue) return renderValue(soleValue, option);
      // An unknown value still shows (as the raw value) rather than silently reading as "nothing
      // applied" — a value applied from a URL before any options have loaded gets here.
      return option?.label ?? soleValue;
    })();

    const isActive = active ?? (resolvedValue != null || selectedCount > 0);

    const renderOptions = () =>
      (options ?? []).map(opt => {
        const isSelected = multi ? selectedArray.includes(opt.value) : selectedSingle === opt.value;

        if (multi) {
          return (
            <MenuItem
              key={opt.value}
              // Menu pattern: the ROW is the checkable control (MUI Menu supplies arrow-key focus
              // + Enter/Space activation) — `menuitemcheckbox` announces the checked state, same
              // family as the single-select's `menuitemradio` below.
              role="menuitemcheckbox"
              aria-checked={isSelected}
              selected={isSelected}
              disabled={opt.disabled}
              onClick={event => handleToggleMultiple(opt.value, event)}
            >
              {/* Presentational only — the MenuItem row owns the click; the input is readOnly
                (no double-toggle, no React "controlled without onChange" warning) and removed
                from the tab/a11y tree since the option row already conveys selection. */}
              <Checkbox
                checked={isSelected}
                disableRipple
                slotProps={{ input: { readOnly: true, tabIndex: -1, 'aria-hidden': true } }}
              />
              {/* The same text block the panel uses, so an option carrying a `description` shows
                it on EITHER surface. Rendering `opt.label` alone here would drop the subline
                silently depending on which surface the filter happened to open. */}
              <ListItemText primary={opt.label} secondary={opt.description} sx={{ margin: 0 }} />
            </MenuItem>
          );
        }

        return (
          <MenuItem
            key={opt.value}
            role="menuitemradio"
            aria-checked={isSelected}
            selected={isSelected}
            disabled={opt.disabled}
            onClick={event => handleSelectSingle(opt.value, event)}
          >
            <ListItemText primary={opt.label} secondary={opt.description} sx={{ margin: 0 }} />
          </MenuItem>
        );
      });

    // Inline-Menu slots, unpacked so the consumer's object-form entries can be merged UNDER this
    // component's ARIA wiring rather than replacing the whole `slotProps` object. Callback-form
    // entries are dropped on purpose (documented on the prop): merging a function would mean
    // calling it with an ownerState this component does not own.
    const { slotProps: consumerMenuSlotProps, ...menuRest } = slotProps?.menu ?? {};
    const consumerMenuList = typeof consumerMenuSlotProps?.list === 'object' ? consumerMenuSlotProps.list : undefined;
    const consumerMenuPaper =
      typeof consumerMenuSlotProps?.paper === 'object' ? consumerMenuSlotProps.paper : undefined;

    return (
      <>
        <FilterTrigger
          ref={ref}
          id={buttonId}
          label={label}
          // FilterTrigger prefers the value over the count, so passing both is safe — but keep them
          // gated on `isActive` so `active={false}` really does read as idle.
          value={isActive ? resolvedValue : undefined}
          count={isActive ? selectedCount : undefined}
          maxWidth={maxWidth}
          active={isActive}
          open={open}
          aria-haspopup={panel ? 'dialog' : 'menu'}
          aria-expanded={open}
          aria-controls={open ? surfaceId : undefined}
          onClick={handleOpen}
          {...rest}
        />

        {panel ? (
          <FilterDropdownPanel
            searchable={searchable}
            searchPlaceholder={searchPlaceholder}
            applyLabel={applyLabel}
            clearLabel={clearLabel}
            noResultsText={noResultsText}
            loading={loading}
            onSearchChange={onSearchChange}
            searchDebounceMs={searchDebounceMs}
            minSearchLength={minSearchLength}
            hintText={hintText}
            loadingLabel={loadingLabel}
            {...slotProps?.panel}
            // `paper` is hoisted to the button's own slot so one spelling works in BOTH modes;
            // merged over anything the consumer also passed through `panel.slotProps`.
            slotProps={{
              ...slotProps?.panel?.slotProps,
              paper: { ...slotProps?.panel?.slotProps?.paper, ...slotProps?.paper },
            }}
            id={surfaceId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            options={options ?? []}
            // One prop covers both models: single-select passes its applied value as a 0-or-1
            // length array rather than the panel growing a second selection prop.
            selected={multi ? selectedArray : selectedSingle != null ? [selectedSingle] : []}
            multiple={multi}
            onApply={(next, event) => {
              commit(next, event);
              handleClose();
            }}
            onClearAll={event => commit([], event)}
            // Same gate as the inline Menu — one deselect rule for the whole family, so the two
            // surfaces of the same filter can't behave differently.
            onSelect={(next, event) => handleSelectSingle(next, event)}
            labelledBy={buttonId}
          />
        ) : (
          <Menu
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            {...menuRest}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              ...consumerMenuSlotProps,
              paper: { ...consumerMenuPaper, ...slotProps?.paper },
              list: {
                // Consumer first: an `sx`, a `data-testid`, a `dense` — all fine.
                ...consumerMenuList,
                // The trigger's `aria-controls` must point at the LIST (the element it controls),
                // not the Menu's Modal root. Last, so it cannot be overridden away.
                id: surfaceId,
                'aria-labelledby': buttonId,
                // Items are `menuitemcheckbox` (multi) / `menuitemradio` (single) — both belong
                // in a `menu` list (NOT `listbox`, which expects focusable `option` children).
                role: 'menu',
              },
            }}
          >
            {/* Same answer as the panel, so the family has ONE loading look: the surface opens and
              shows skeleton rows. `MenuItem` wrapping them keeps the rows on the list's own
              metrics; `disabled` keeps them out of arrow-key navigation, and the list is marked
              busy so a screen reader is told once rather than five times. */}
            {loading ? (
              <MenuItem disabled aria-busy="true" aria-label={loadingLabel ?? 'Loading options'}>
                <StyledMenuLoadingRows>
                  {MENU_SKELETON_ROWS.map(row => (
                    <Skeleton key={row} variant="rounded" width={row} height={sizes.height.h16} />
                  ))}
                </StyledMenuLoadingRows>
              </MenuItem>
            ) : (
              (menuContent ?? renderOptions())
            )}
          </Menu>
        )}
      </>
    );
  }
);
